/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useParams, useSearchParams } from "react-router-dom";
import {
    AlertDialog,
    AlertDialogContent,
    Badge,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    Pagination,
    Table,
} from "@jhool-io/fe-components";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { IClientProviderHistory } from "../../types/clients.types";
import { APP_COLORS, LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";

import {
    convertTimeTo12HoursFormat,
    displayNameInRightFormat,
    formatDate,
    formatZonedTimeToUtc,
    getTotalAmountOfTableRowsSelected,
    handleFormatDatePickerValue,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import { ProviderStatus } from "../../../../utils/types/session";
import { useFetchClientProviderHistory } from "../../hooks/clients.queries";
import Skeleton from "../../../../components/Skeleton/Skeleton";
import ListState from "../../../../components/ListState/ListState";
import MobileListItem from "../../../../shared-ui/MobileListItem/MobileListItem";
import { getProviderCredenetialPillStyle } from "../../../providers/helpers/providers.helper";
import AddClientSession from "../AddClientSession/AddClientSession";
import MoreButton from "../../../../shared-ui/Buttons/MoreButton/MoreButton";
import EditClientSession from "../EditClientSession/EditClientSession";
import {
    useDeleteClientSession,
    useEditClientSession,
} from "../../hooks/clients.mutations";
import useToast from "../../../../hooks/useToast";
import TrashIcon from "../../../../components/Icons/Trash";

type Dialogs =
    | "add-session"
    | "edit-session"
    | "delete-session"
    | "bulk-delete-sessions";

export default function ClientProvidersHistoryList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );

    const [rowSelection, setRowSelection] = React.useState({});
    const [getSelectedSessionData, setGetSelectedSessionData] = React.useState<
        Row<IClientProviderHistory>[]
    >([]);

    const [selectedSessionId, setSelectedSessionId] = React.useState<
        string | null
    >(null);

    const params = useParams();
    const clientId = params.clientId as string;

    const deleteSession = useDeleteClientSession(clientId);

    const editSession = useEditClientSession(clientId || "");

    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;
    const pageFilter = Number(searchParams.get("page")) || 1;

    const { isLoading, data, error, isSuccess } = useFetchClientProviderHistory(
        clientId,
        { limit: limitFilter, page: pageFilter }
    );

    const queryClient = useQueryClient();
    const { toast } = useToast();

    const selectedSessionsIds =
        getSelectedSessionData &&
        getSelectedSessionData.map(
            (item: Row<IClientProviderHistory>) => item.original.session_id
        );

    // Details for a single session
    const sessionDetails = data?.data.find((session) => {
        return session.session_id === selectedSessionId;
    });

    const handleCloseDialog = () => {
        setSelectedSessionId(null);
        setDialogInView(null);
    };

    const handleCloseBulkDeleteDialog = () => {
        setSelectedSessionId(null);
        setDialogInView(null);
        setRowSelection({});
    };

    const handleShowEditDialog = () => {
        setDialogInView("edit-session");
    };

    const handleShowDeleteDialog = () => {
        setDialogInView("delete-session");
    };

    const handleChangeStatus = () => {
        if (
            sessionDetails?.appointment.session_start_hr &&
            sessionDetails?.appointment.session_end_hr
        ) {
            const formattedStartTime = formatZonedTimeToUtc(
                `${handleFormatDatePickerValue(
                    sessionDetails.first_session_date
                )}T${
                    sessionDetails?.appointment.session_start_hr < 10
                        ? `0${sessionDetails?.appointment.session_start_hr}`
                        : sessionDetails?.appointment.session_start_hr
                }:${
                    sessionDetails?.appointment.session_start_min < 10
                        ? `0${sessionDetails?.appointment.session_start_min}`
                        : sessionDetails?.appointment.session_start_min
                }:00.000`
            );

            const formattedEndTime = formatZonedTimeToUtc(
                `${handleFormatDatePickerValue(
                    sessionDetails.first_session_date
                )}T${
                    sessionDetails?.appointment.session_end_hr < 10
                        ? `0${sessionDetails?.appointment.session_end_hr}`
                        : sessionDetails?.appointment.session_end_hr
                }:${
                    sessionDetails?.appointment.session_end_min < 10
                        ? `0${sessionDetails?.appointment.session_end_min}`
                        : sessionDetails?.appointment.session_end_min
                }:00.000`
            );

            const payload = {
                sessions: [
                    {
                        session_start_time: formattedStartTime,
                        session_end_time: formattedEndTime,
                        session_id: sessionDetails?.session_id || "",
                        status: sessionDetails?.is_current
                            ? "inactive"
                            : "active",
                        start_date: sessionDetails.first_session_date,
                        recurrence_rule:
                            sessionDetails.appointment.recurrence_rule,
                    },
                ],
            };
            editSession.mutate(payload, {
                onSuccess: async () => {
                    await queryClient.invalidateQueries({
                        queryKey: [clientId, "provider-history"],
                    });
                    toast({
                        mode: "success",
                        message: "Session cancelled successfully",
                    });
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not edit session at this time.",
                    });
                },
            });
        }
    };

    const handleDeleteSession = () => {
        if (selectedSessionId) {
            const dataToSend = { session_ids: [selectedSessionId] };
            deleteSession.mutate(dataToSend, {
                onSuccess: async (res) => {
                    await queryClient.invalidateQueries({
                        queryKey: [clientId, "provider-history"],
                    });
                    toast({
                        mode: "success",
                        message: res.message || "Session deleted successfully",
                    });
                    handleCloseDialog();
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not delete session, please try again",
                    });
                },
            });
        }
    };

    const handleBulkDeleteSession = () => {
        if (selectedSessionsIds) {
            const dataToSend = {
                session_ids:
                    selectedSessionsIds && selectedSessionsIds.length > 0
                        ? selectedSessionsIds
                        : [],
            };
            deleteSession.mutate(dataToSend, {
                onSuccess: async (res) => {
                    await queryClient.invalidateQueries({
                        queryKey: [clientId, "provider-history"],
                    });
                    toast({
                        mode: "success",
                        message: res.message || "Sessions deleted successfully",
                    });
                    handleCloseBulkDeleteDialog();
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not delete sessions, please try again",
                    });
                },
            });
        }
    };

    const handleSetPageFilter = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleSetLimitFilter = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    const columns: ColumnDef<IClientProviderHistory>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    handleChange={table.getToggleAllRowsSelectedHandler()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={table.getIsAllRowsSelected()}
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    handleChange={row.getToggleSelectedHandler()}
                    indeterminate={row.getIsSomeSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={row.getIsSelected()}
                />
            ),
        },
        {
            accessorKey: "provider_name",
            header: "PROVIDER NAME",
            cell: ({ row }) => {
                return (
                    <span className="flex flex-col gap-y-4">
                        <span className="flex items-center gap-x-4 capitalize">
                            {displayNameInRightFormat(
                                row.original.provider_name
                            )}
                            {row.original.is_current ? (
                                <>
                                    <svg
                                        width="2"
                                        height="2"
                                        viewBox="0 0 2 2"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle
                                            cx="1"
                                            cy="1"
                                            r="1"
                                            fill={APP_COLORS.COLOR_PRIMARY}
                                        />
                                    </svg>
                                    <span className="text-primary">
                                        Current
                                    </span>
                                </>
                            ) : null}
                        </span>
                        {row.original.credential && (
                            <span>
                                {getProviderCredenetialPillStyle(
                                    row.original.credential
                                )}
                            </span>
                        )}
                    </span>
                );
            },
        },
        {
            accessorKey: "service",
            header: "service",
            cell: ({ row }) =>
                makeStringFirstLetterCapital(
                    removeEnumUnderscore(row.original.service)
                ),
        },

        {
            accessorKey: "no_of_sessions",
            header: "NO. OF SESSIONS",
            cell: ({ row }) => row.original.no_of_sessions || "--",
        },
        {
            accessorKey: "first_session_date",
            header: "FIRST SESSION DATE",
            cell: ({ row }) => formatDate(row.original.first_session_date),
        },
        {
            accessorKey: "last_seen",
            header: "LAST SEEN",
            cell: ({ row }) => formatDate(row.original.last_seen),
        },
        {
            accessorKey: "appointment",
            header: "APPOINTMENT",
            cell: ({ row }) =>
                `${
                    row.original.appointment.session_day
                } ${convertTimeTo12HoursFormat(
                    `${row.original.appointment.session_start_hr}:${row.original.appointment.session_start_min}`
                )}, ${makeStringFirstLetterCapital(
                    row.original.appointment.recurrence_rule
                )}`,
        },
        {
            accessorKey: "status",
            header: "SESSION STATUS",
            cell: ({ row }) => {
                return row.original.is_current ? (
                    <Badge
                        content={ProviderStatus.ACTIVE}
                        textColor="#00563E"
                        bgColor="rgba(204, 250, 233, 0.50)"
                        className="capitalize font-normal px-8 py-4 h-24 rounded-r4"
                    />
                ) : (
                    <Badge
                        content="Terminated"
                        textColor="#981F41"
                        bgColor="rgba(251, 199, 198, 0.50)"
                        className="capitalize font-normal px-8 py-4 h-24 rounded-r4"
                    />
                );
            },
        },
        {
            accessorKey: "session_id",
            header: "",
            cell: ({ row }) => (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <MoreButton
                            onClick={() =>
                                setSelectedSessionId(row.original.session_id)
                            }
                        />
                    </DropdownTrigger>
                    <DropdownContent width="auto" align="end">
                        <DropdownItem
                            onClick={(e) => {
                                e.stopPropagation();
                                handleShowEditDialog();
                            }}
                        >
                            Edit session
                        </DropdownItem>
                        <DropdownItem
                            onClick={(e) => {
                                e.stopPropagation();
                                handleShowDeleteDialog();
                            }}
                        >
                            Delete session
                        </DropdownItem>
                        {row.original.is_current ? (
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleChangeStatus();
                                }}
                            >
                                Cancel session
                            </DropdownItem>
                        ) : null}
                        {!row.original.is_current ? (
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleChangeStatus();
                                }}
                            >
                                Activate session
                            </DropdownItem>
                        ) : null}
                    </DropdownContent>
                </Dropdown>
            ),
        },
    ];

    const isAddingSession = useIsMutating(["add-session"]);
    const isEditMutationRunning = useIsMutating(["edit-session"]);
    const isDeletingSession = useIsMutating(["delete-session"]);

    return (
        <>
            <AlertDialog open={dialogInView === "bulk-delete-sessions"}>
                <AlertDialogContent
                    onSaveClick={handleBulkDeleteSession}
                    saveText="Delete Sessions"
                    onCancelClick={handleCloseBulkDeleteDialog}
                    title="Delete Sessions"
                    description="Are you sure you want to delete the selected sessions?"
                    isSaveBtnDisabled={isDeletingSession > 0}
                    isCancelBtnDisabled={isDeletingSession > 0}
                />
            </AlertDialog>
            <Dialog open={dialogInView === "add-session"}>
                <DialogContent
                    title="Add Session"
                    showFooter
                    saveText="Add Session"
                    cancelText="Cancel"
                    variant="center"
                    handleCloseDialog={() => handleCloseDialog()}
                    submitBtnFormValue="add-session"
                    isCancelBtnDisabled={isAddingSession > 0}
                    isSubmitBtnDisabled={isAddingSession > 0}
                >
                    <AddClientSession
                        onFormSubmit={() => handleCloseDialog()}
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "edit-session"}>
                <DialogContent
                    title="Edit session"
                    showFooter
                    saveText="Save changes"
                    cancelText="Cancel"
                    variant="center"
                    handleCloseDialog={() => handleCloseDialog()}
                    submitBtnFormValue="edit-session"
                    isCancelBtnDisabled={isEditMutationRunning > 0}
                    isSubmitBtnDisabled={isEditMutationRunning > 0}
                >
                    <EditClientSession
                        onFormSubmit={() => handleCloseDialog()}
                        sessionDetails={sessionDetails}
                    />
                </DialogContent>
            </Dialog>
            <AlertDialog open={dialogInView === "delete-session"}>
                <AlertDialogContent
                    onSaveClick={() => handleDeleteSession()}
                    saveText="Delete Session"
                    onCancelClick={() => handleCloseDialog()}
                    title="Delete Session"
                    description="Are you sure you want to delete this session?"
                    isSaveBtnDisabled={isDeletingSession > 0}
                    isCancelBtnDisabled={isDeletingSession > 0}
                />
            </AlertDialog>

            <div className="flex justify-end flex-wrap gap-y-2 rounded-r8 bg-white border border-stroke-divider px-12 py-24 md:p-24">
                {" "}
                <Button onClick={() => setDialogInView("add-session")}>
                    Add Session
                </Button>
            </div>
            <div className="mt-16">
                {isLoading && (
                    <Skeleton
                        type="table"
                        containerTestId="providerslist-loader"
                    />
                )}
                {error && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error.response?.data.message ||
                            `Cannot display client providers list at this time please try again later`
                        }
                    />
                )}
                {data && isSuccess && data.data.length === 0 && (
                    <ListState
                        emptyMessage="No provider history yet"
                        isError={false}
                        stateHelperText="Client provider history will appear here when added"
                    />
                )}
                {data && isSuccess && data.data.length > 0 && (
                    <>
                        {getTotalAmountOfTableRowsSelected(rowSelection)
                            .amount > 0 && (
                            <div className="flex items-center mb-16 gap-x-12">
                                <div className="font-medium text-base">
                                    {
                                        getTotalAmountOfTableRowsSelected(
                                            rowSelection
                                        ).amountString
                                    }
                                    :
                                </div>
                                <Button
                                    variant="secondary"
                                    className="gap-x-8"
                                    onClick={() =>
                                        setDialogInView("bulk-delete-sessions")
                                    }
                                >
                                    <TrashIcon /> Delete sessions
                                </Button>
                            </div>
                        )}
                        <div className="hidden md:block">
                            <Table
                                data={data.data}
                                columns={columns}
                                hasPagination={data.total_count > 20}
                                setRowSelection={setRowSelection}
                                rowSelection={rowSelection}
                                setGetSelectedData={setGetSelectedSessionData}
                                pagination={
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        currentPage={pageFilter}
                                        onPageChange={handleSetPageFilter}
                                        limit={limitFilter}
                                        onLimitChange={handleSetLimitFilter}
                                    />
                                }
                            />
                        </div>
                        <div className="block md:hidden" role="table">
                            <div className="flex flex-col gap-y-12">
                                {data.data.map((provider) => (
                                    <MobileListItem
                                        key={provider.provider_id}
                                        showCheckButton={false}
                                        topChildren={
                                            <div className="flex flex-col gap-y-4">
                                                <div className="flex items-center gap-x-4 capitalize font-semibold text-base">
                                                    {displayNameInRightFormat(
                                                        provider.provider_name
                                                    )}
                                                    {provider.is_current ? (
                                                        <>
                                                            <svg
                                                                width="2"
                                                                height="2"
                                                                viewBox="0 0 2 2"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <circle
                                                                    cx="1"
                                                                    cy="1"
                                                                    r="1"
                                                                    fill={
                                                                        APP_COLORS.COLOR_PRIMARY
                                                                    }
                                                                />
                                                            </svg>
                                                            <span className="text-primary">
                                                                Current
                                                            </span>
                                                        </>
                                                    ) : null}
                                                </div>
                                                <span>
                                                    {getProviderCredenetialPillStyle(
                                                        provider.credential
                                                    )}
                                                </span>
                                            </div>
                                        }
                                        bottomChildren={
                                            <div className="flex flex-col gap-y-8 w-full items-start">
                                                <div className="flex items-center w-full text-left gap-x-4">
                                                    <div className="basis-1/2">
                                                        <span className="block font-medium text-xss uppercase">
                                                            service
                                                        </span>
                                                        <span className="block text-gray font-semibold text-xs">
                                                            {makeStringFirstLetterCapital(
                                                                removeEnumUnderscore(
                                                                    provider.service
                                                                )
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div className="basis-1/2">
                                                        <span className="block font-medium text-xss uppercase">
                                                            No of sessions
                                                        </span>
                                                        <span className="block text-gray font-semibold text-xs">
                                                            {
                                                                provider.no_of_sessions
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center w-full text-left gap-x-4">
                                                    <div className="basis-1/2">
                                                        <span className="block font-medium text-xss uppercase">
                                                            first session date
                                                        </span>
                                                        <span className="block text-gray font-semibold text-xs">
                                                            {formatDate(
                                                                provider.first_session_date
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div className="basis-1/2">
                                                        <span className="block font-medium text-xss uppercase">
                                                            last seen
                                                        </span>
                                                        <span className="block text-gray font-semibold text-xs">
                                                            {formatDate(
                                                                provider.last_seen
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center w-full text-left gap-x-4">
                                                    <div className="basis-1/2">
                                                        <span className="block font-medium text-xss uppercase">
                                                            appointment
                                                        </span>
                                                        <span className="block text-gray font-semibold text-xs">
                                                            {`${
                                                                provider
                                                                    .appointment
                                                                    .session_day
                                                            } ${convertTimeTo12HoursFormat(
                                                                `${provider.appointment.session_start_hr}:${provider.appointment.session_start_min}`
                                                            )}, ${makeStringFirstLetterCapital(
                                                                provider
                                                                    .appointment
                                                                    .recurrence_rule
                                                            )}`}
                                                        </span>
                                                    </div>
                                                    <div className="basis-1/2">
                                                        <span className="block font-medium text-xss uppercase">
                                                            session status
                                                        </span>
                                                        <span className="block text-gray font-semibold text-xs">
                                                            {provider.is_current ? (
                                                                <Badge
                                                                    content={
                                                                        ProviderStatus.ACTIVE
                                                                    }
                                                                    textColor="#00563E"
                                                                    bgColor="rgba(204, 250, 233, 0.50)"
                                                                    className="capitalize font-normal px-8 py-4 h-24 rounded-r4"
                                                                />
                                                            ) : (
                                                                <Badge
                                                                    content="TERMINATED"
                                                                    textColor="#981F41"
                                                                    bgColor="rgba(251, 199, 198, 0.50)"
                                                                    className="capitalize font-normal px-8 py-4 h-24 rounded-r4"
                                                                />
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                    />
                                ))}
                            </div>
                            {data.total_count > 20 && (
                                <div className="bg-white rounded-r8 p-12 mt-16">
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        currentPage={pageFilter}
                                        onPageChange={handleSetPageFilter}
                                        limit={limitFilter}
                                        onLimitChange={handleSetLimitFilter}
                                    />
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
